<template>
  <div>
  <v-dialog v-model="show" max-width="900" persistent scrollable>
    <v-card>
      <v-toolbar dark color="primary" dense flat>
        <v-toolbar-title>{{ use_language.pack_products }}</v-toolbar-title>
        <v-spacer />
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="openPrinterSetup">
              <v-icon>mdi-printer-settings</v-icon>
            </v-btn>
          </template>
          <span>{{ use_language.printer_setting }}</span>
        </v-tooltip>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <div class="d-flex align-center flex-wrap" style="gap:12px">
          <v-text-field
            v-model="scanCode"
            ref="scanOrder"
            autocomplete="off"
            outlined dense hide-details
            label="OrderID / TrackingNo"
            :disabled="loading"
            style="max-width:340px"
            @focus="$event.target.select()"
            @keyup.enter="searchOrder"
          />
          <v-btn color="primary" :loading="loading" @click="searchOrder">{{ use_language.search }}</v-btn>
          <v-btn text v-if="show_pack" @click="clearOrder">{{ use_language.clear }}</v-btn>
        </div>

        <template v-if="show_pack && orderInfo">
          <v-card outlined class="my-4" style="border-radius:12px">
            <v-card-text class="pa-4">
              <v-row no-gutters>
                <v-col cols="12" md="8" class="pr-md-4">
                  <div class="d-flex align-center mb-3">
                    <img v-if="orderInfo.channelLogo" :src="orderInfo.channelLogo" width="40" height="40"
                      class="mr-3" style="border-radius:8px" />
                    <div class="d-flex flex-column" style="line-height:1.2">
                      <span class="text-h6 font-weight-bold">PA{{ orderInfo.id }}</span>
                      <span class="grey--text text--darken-1" style="font-size:12.5px">{{ orderInfo.referenceNumber }}</span>
                    </div>
                  </div>

                  <div v-if="orderInfo.receiverAddress">
                    <div class="d-flex align-center flex-wrap mb-1" style="gap:4px 16px">
                      <span class="font-weight-bold"><v-icon small class="mr-1" style="margin-top:-2px">mdi-account-outline</v-icon>{{ orderInfo.receiverAddress.name }}</span>
                      <span class="grey--text text--darken-1"><v-icon small class="mr-1" style="margin-top:-2px">mdi-phone-outline</v-icon>{{ orderInfo.receiverAddress.phone }}</span>
                    </div>
                    <div class="d-flex grey--text text--darken-2" style="font-size:13px; line-height:1.5">
                      <v-icon small class="mr-1" style="margin-top:2px">mdi-map-marker-outline</v-icon>
                      <span>{{ receiverFullAddress }}</span>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="mt-3 mt-md-0 d-flex flex-column align-md-end text-md-right">
                  <img v-if="orderInfo.expressCompanyLogo" :src="orderInfo.expressCompanyLogo"
                    style="max-height:28px; max-width:100px; object-fit:contain" />
                  <span v-else class="font-weight-medium grey--text text--darken-3" style="font-size:13px">{{ orderInfo.expressCompanyName || '-' }}</span>
                  <v-chip v-if="orderInfo.isCOD" small color="red" dark label class="mt-2">COD</v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-tabs v-model="activeTab" v-if="packages.length > 1" show-arrows class="mb-2">
            <v-tab v-for="(pkg) in packages" :key="pkg.packageID">{{ use_language.parcel }} {{ pkg.packageNo }}</v-tab>
          </v-tabs>

          <div v-for="(pkg, i) in packages" :key="pkg.packageID" v-show="activeTab === i">
            <div class="d-flex align-center flex-wrap mt-2" style="gap:12px">
              <v-text-field
                v-model="itemCode"
                :ref="'itemScan_' + i"
                autocomplete="off"
                outlined dense hide-details
                :label="use_language.scan_barcode_sku"
                style="max-width:340px"
                @focus="$event.target.select()"
                @keyup.enter="addItem(pkg)"
              />
              <v-btn color="primary" @click="addItem(pkg)">{{ use_language.add }}</v-btn>
            </div>

            <v-simple-table class="mt-3">
              <thead>
                <tr>
                  <th style="width:100px"></th>
                  <th>{{ use_language.product }}</th>
                  <th style="width:140px">SKU</th>
                  <th style="width:160px">Barcode</th>
                  <th class="text-center" style="width:150px">{{ use_language.item }}</th>
                  <th class="text-center" style="width:80px">{{ use_language.all_ }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in pkg.items"
                  :key="item.orderItemID + '_' + item.sku"
                  :style="(item.packedQuantity || 0) >= item.quantity ? 'background-color:#beffc1' : ''"
                >
                  <td class="pa-2">
                    <v-img v-if="item.photoLink" :src="item.photoLink" width="80" height="80"
                      class="rounded" style="border:1px solid #eee" />
                    <div v-else class="d-flex align-center justify-center"
                      style="width:80px;height:80px;background:#f2f2f2;border-radius:6px">
                      <v-icon color="grey lighten-1" size="34">mdi-image-off-outline</v-icon>
                    </div>
                  </td>
                  <td>{{ item.productName }}</td>
                  <td class="font-weight-medium">{{ item.sku }}</td>
                  <td class="grey--text text--darken-1">{{ item.barcode }}</td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <v-btn icon x-small color="grey darken-1"
                        :disabled="(item.packedQuantity || 0) <= 0"
                        @click="item.packedQuantity = Math.max(0, (item.packedQuantity || 0) - 1)">
                        <v-icon>mdi-minus</v-icon>
                      </v-btn>
                      <b class="mx-2" style="min-width:26px;display:inline-block;font-size:14px;font-weight:bold"
                        :class="(item.packedQuantity || 0) >= item.quantity ? 'green--text' : 'red--text'">
                        {{ item.packedQuantity || 0 }}
                      </b>
                      <v-btn icon x-small color="grey darken-1"
                        :disabled="(item.packedQuantity || 0) >= item.quantity"
                        @click="item.packedQuantity = Math.min(item.quantity, (item.packedQuantity || 0) + 1)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </td>
                  <td class="text-center grey--text text--darken-1" style="font-size:14px;font-weight:bold">{{ item.quantity }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-row class="mt-6">
            <v-col cols="12" class="text-center">
              <v-btn large color="success" class="mr-3" :disabled="!isAllPacked || loading" :loading="loading" @click="save(false)">
                <v-icon left>mdi-content-save</v-icon> {{ use_language.button_save }}
              </v-btn>
              <v-btn large color="primary" :disabled="!isAllPacked || loading" :loading="loading" @click="save(true)">
                <v-icon left>mdi-printer</v-icon> {{ use_language.save_and_print_awb }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>
    </v-dialog>

    <v-dialog v-model="agentNotInstalledDialog" :max-width="480">
      <v-card>
        <v-card-title class="height-57">
          <v-row>
            <v-col cols="10"><h5>{{ use_language.print_agent_not_found }}</h5></v-col>
            <v-col cols="2" align="right">
              <v-btn icon @click="agentNotInstalledDialog = false">
                <v-icon color="danger">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div class="text-center mb-3">
            <v-icon color="warning" size="56">mdi-printer-off</v-icon>
          </div>
          <div class="mb-2">
            {{ use_language.cannot_connect_to }} <b>Print Agent</b> {{ use_language.possible_causes }}:
          </div>
          <ul class="ml-3 mb-3">
            <li>{{ use_language.print_agent_not_installed }}</li>
            <li>{{ use_language.print_agent_not_running }}</li>
            <li>{{ use_language.print_agent_blocked }}</li>
          </ul>
          <div>{{ use_language.print_agent_install_hint }}</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="agentNotInstalledDialog = false">{{ use_language.close }}</v-btn>
          <v-btn color="primary" @click="downloadPrintAgent">
            <v-icon left>mdi-download</v-icon> {{ use_language.download_print_agent }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="printerSetupDialog" scrollable :max-width="500" persistent>
      <v-card elevation="1">
        <v-card-title class="height-57">
          <v-row>
            <v-col cols="8"><h5>{{ use_language.select_printer_for_awb }}</h5></v-col>
            <v-col cols="4" align="right">
              <v-btn icon @click="printerSetupDialog = false">
                <v-icon color="danger">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert v-if="!printAgentReady" type="info" dense outlined class="mb-3">
            {{ use_language.print_agent_not_connected }}
            <a href="javascript:void(0)" @click="connectPrintAgent">{{ use_language.reconnect }}</a>
            {{ use_language.or_print_via_browser }}
          </v-alert>

          <v-alert v-if="printerMissingWarning" type="warning" dense outlined class="mb-3">
            <span v-if="printerMissingReason === 'offline'">
              {{ use_language.printer_offline_warning }}
            </span>
            <span v-else>{{ use_language.printer_notfound_warning }}</span>
          </v-alert>

          <div v-if="loadingPrinters" class="text-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">{{ use_language.loading_printer_list }}</div>
          </div>
          <div v-else-if="availablePrinters.length === 0" class="text-center pa-4">
            <v-icon color="warning" size="48">mdi-printer-off</v-icon>
            <div class="mt-2">{{ use_language.no_printer_found }}</div>
            <div class="text-caption grey--text">{{ use_language.check_print_agent_running }}</div>
          </div>
          <v-radio-group v-else v-model="selectedPrinterInDialog" class="mt-2" hide-details>
            <v-radio
              v-for="p in availablePrinters"
              :key="p.name"
              :value="p.name"
              :label="p.name + (p.isDefault ? ' (' + use_language.default + ')' : '')"
            ></v-radio>
          </v-radio-group>

          <v-divider class="my-3"></v-divider>

          <div class="text-caption grey--text mb-1">{{ use_language.paper_size }}</div>
          <v-radio-group v-model="selectedLabelSizeInDialog" hide-details class="mt-0">
            <v-radio
              v-for="preset in labelSizePresets"
              :key="preset.id"
              :value="preset.id"
              :label="preset.name"
            ></v-radio>
            <v-radio value="custom" :label="use_language.custom"></v-radio>
          </v-radio-group>
          <div v-if="selectedLabelSizeInDialog === 'custom'" class="d-flex align-center mt-1 ml-8">
            <v-text-field
              v-model.number="customWidthMm"
              type="number" suffix="mm" hide-details dense
              style="max-width: 110px" :placeholder="use_language.cubic1"
            ></v-text-field>
            <span class="mx-2">×</span>
            <v-text-field
              v-model.number="customHeightMm"
              type="number" suffix="mm" hide-details dense
              style="max-width: 110px" :placeholder="use_language.cubic3"
            ></v-text-field>
          </div>

          <div class="text-center mt-3">
            <v-btn color="grey" text @click="loadPrinterList" :disabled="loadingPrinters">
              <v-icon left>mdi-refresh</v-icon> {{ use_language.refresh }}
            </v-btn>
            <v-btn color="primary" @click="savePrinterSelection" :disabled="!selectedPrinterInDialog || loadingPrinters">
              {{ use_language.button_save }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import { AlertSuccess, AlertError, AlertWarning } from '@/website/global_alert'
import { orderService_dotnet, stockService_dotnet, ecomService_dotnet, logistic_Service } from '@/website/global'
import { get_languages } from '@/website/global_function'
import { PDFDocument } from 'pdf-lib'
import { printAgent, LABEL_SIZE_PRESETS, parseLabelSize } from '@/utils/printAgent'
import { pdfToImages } from '@/utils/pdfToImages'
import buzzerSound from './buzzer.wav'
import successSound from './success_scan.mp3'

export default {
  name: 'PackProductDialog',
  props: {
    value: { type: Boolean, default: false },
  },
  data () {
    return {
      shopID: localStorage.getItem('shop_id'),
      staffShopID: localStorage.getItem('staff_id'),
      branchID: localStorage.getItem('branch_id'),
      header_token: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('authorization_token'),
      },
      use_language: {},

      scanCode: '',
      itemCode: '',
      loading: false,
      show_pack: false,
      orderInfo: null,
      packages: [],
      activeTab: 0,
      productSetCache: {},

      audioBuzzer: new Audio(localStorage.getItem('alert_error_url') || buzzerSound),
      audioSuccess: new Audio(localStorage.getItem('alert_success_url') || successSound),

      printAgentReady: false,
      connectingAgent: false,
      unsubscribeAgentState: null,
      agentNotInstalledDialog: false,
      printAgentDownloadUrl: 'https://packhaistorage.s3.ap-southeast-1.amazonaws.com/PackhaiPrintAgentSetup.zip',
      labelPrinterName: '',
      labelSize: '100x150',
      labelSizePresets: LABEL_SIZE_PRESETS,
      customWidthMm: 100,
      customHeightMm: 150,
      printerSetupDialog: false,
      printerMissingWarning: false,
      printerMissingReason: '',
      printerStatus: 'unknown',
      printerHealthInterval: null,
      availablePrinters: [],
      selectedPrinterInDialog: null,
      selectedLabelSizeInDialog: '100x150',
      loadingPrinters: false,
    }
  },
  computed: {
    show: {
      get () { return this.value },
      set (v) { this.$emit('input', v) },
    },
    isAllPacked () {
      if (!this.packages || this.packages.length === 0) return false
      return this.packages.every(pkg =>
        pkg.items && pkg.items.length > 0 &&
        pkg.items.every(it => (it.packedQuantity || 0) >= it.quantity)
      )
    },
    receiverFullAddress () {
      const r = this.orderInfo && this.orderInfo.receiverAddress
      if (!r) return ''
      return [r.address, r.subDistrict, r.district, r.province, r.postcode]
        .filter(v => v && String(v).trim() !== '').join(' ')
    },
  },
  watch: {
    value (v) {
      if (v) {
        this.clearOrder()
        this.$nextTick(() => { this.focusScanOrder() })
        this.ensurePrinterOnOpen()
      }
    },
  },
  mounted () {
    this.unsubscribeAgentState = printAgent.onStateChange((newState) => {
      this.printAgentReady = newState === 'ready'
      if (newState !== 'ready') this.stopPrinterHealthCheck()
    })
    this.checkPrintAgent()
  },
  beforeDestroy () {
    if (this.unsubscribeAgentState) {
      this.unsubscribeAgentState()
      this.unsubscribeAgentState = null
    }
    this.stopPrinterHealthCheck()
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
  },
  methods: {
    get_languages,
    focusScanOrder () {
      const el = this.$refs.scanOrder
      if (el && el.focus) el.focus()
    },
    focusItemScan () {
      this.$nextTick(() => {
        const ref = this.$refs['itemScan_' + this.activeTab]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.focus) el.focus()
      })
    },
    parseScan (code) {
      const c = (code || '').trim()
      const res = { orderMasterID: 0, packageID: 0, trackingNo: '' }
      if (!c) return res
      const upper = c.toUpperCase()
      if (upper.startsWith('PA')) {
        res.orderMasterID = parseInt(c.substring(2)) || 0
      } else if (upper.startsWith('PX')) {
        res.packageID = parseInt(c.substring(2)) || 0
      } else if (c.length >= 10) {
        res.trackingNo = c
      } else if (!isNaN(parseInt(c))) {
        res.orderMasterID = parseInt(c)
      } else {
        res.trackingNo = c
      }
      return res
    },

    async searchOrder () {
      if (!this.scanCode || this.scanCode.trim() === '') return
      const p = this.parseScan(this.scanCode)
      this.loading = true
      try {
        const check = await this.checkOrderStatusBeforePack(p.orderMasterID, p.packageID, p.trackingNo)
        if (!check || check.isStop) {
          this.loading = false
          if (check && check.isStop) {
            AlertWarning(check.description || this.use_language.cannot_pack_this_order)
          }
          return
        }
        if (check.isConfirm) {
          const confirm = await Swal.fire({
            title: this.use_language.confirm_packing,
            html: (check.description || this.use_language.confirm_repack + ' ?').replace(/\r?\n/g, '<br>'),
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: this.use_language.yes,
            cancelButtonText: this.use_language.no,
          })
          if (!confirm.value) {
            this.loading = false
            return
          }
        }
        const orderMasterID = check.orderMasterID || p.orderMasterID
        if (!orderMasterID) {
          this.loading = false
          AlertError(this.use_language.order_not_found_label)
          return
        }

        const [master, pack] = await Promise.all([
          this.getOrderMasterForPack(orderMasterID),
          this.getOrderPackageForPack(orderMasterID, check.packageID || p.packageID || 0),
        ])

        if (!master || master.status !== 'success' || !pack || pack.status !== 'success') {
          this.loading = false
          AlertError(this.use_language.load_order_data_failed)
          return
        }

        this.orderInfo = master.data
        this.packages = (pack.Packages || pack.packages || []).map(pkg => {
          pkg.items = (pkg.items || []).map(it => {
            if (it.packedQuantity == null) it.packedQuantity = 0
            return it
          })
          return pkg
        })
        this.activeTab = 0
        this.show_pack = true
        this.scanCode = ''
        this.focusItemScan()
      } catch (e) {
        AlertError(this.use_language.error_occurred_try_again)
      } finally {
        this.loading = false
      }
    },

    async addItem (pkg) {
      let code = (this.itemCode || '').trim()
      if (code === '') return
      if (this.runCommand(code)) { this.itemCode = ''; return }
      let qty = 1

      for (const sep of ['x', '=']) {
        if (code.toLowerCase().includes(sep)) {
          const parts = code.toLowerCase().split(sep)
          if (parts.length === 2 && !isNaN(parseInt(parts[1]))) {
            code = parts[0].trim()
            qty = parseInt(parts[1])
          }
        }
      }

      // 1) หาในลิสต์ที่โชว์บน dialog ก่อน — เจอ = +qty เลย (ไม่ยิง API)
      const found = pkg.items.find(it => it.barcode === code || it.sku === code)
      if (found) {
        const next = (found.packedQuantity || 0) + qty
        if (next > found.quantity) {
          this.alertBeep(this.use_language.quantity_exceed_ordered)
        } else {
          found.packedQuantity = next
          this.playSuccess()
        }
        this.itemCode = ''
        this.focusItemScan()
        return
      }

      // 2) ไม่เจอในลิสต์ -> ยิง API ดู ProductMasterItemBarcode (set/ห่อ) -> เทียบ SKU + บวก Piece
      const cacheKey = this.orderInfo.shopID + '_' + code
      let setRes = this.productSetCache[cacheKey]
      if (!setRes) {
        this.loading = true
        setRes = await this.getProductSetByScan(code)
        this.loading = false
        if (setRes && setRes.status === 'success' && setRes.isProductSet && setRes.components && setRes.components.length) {
          this.productSetCache[cacheKey] = setRes
        }
      }
      if (setRes && setRes.status === 'success' && setRes.isProductSet && setRes.components && setRes.components.length) {
        this.applyProductSet(pkg, setRes)
        this.itemCode = ''
        this.focusItemScan()
        return
      }

      // 3) ไม่เจอทั้งลิสต์และ ProductMasterItemBarcode -> เช็ค error
      this.loading = true
      const stock = await this.getStockShopByPack(code)
      this.loading = false
      if (stock && stock.status === 'success' && stock.item) {
        if (String(stock.item.shopID) !== String(this.orderInfo.shopID)) {
          this.alertBeep(this.use_language.not_product_of_this_shop)
        } else {
          this.alertBeep(this.use_language.product_not_in_order)
        }
      } else {
        this.alertBeep(this.use_language.product_not_found)
      }
      this.itemCode = ''
      this.focusItemScan()
    },
    applyProductSet (pkg, setRes) {
      const piece = setRes.piece || 0
      if (piece <= 0) { this.alertBeep(this.use_language.incorrect_quantity_per_pack); return }
      const plan = []
      for (const c of setRes.components) {
        const found = pkg.items.find(it => it.productMasterItemID === c.productMasterItemID)
        if (!found) {
          this.alertBeep(this.use_language.parcel_not_in_order + ': ' + (c.sku || c.barcode || ''))
          return
        }
        const next = (found.packedQuantity || 0) + piece
        if (next > found.quantity) {
          this.alertBeep(this.use_language.excess_pack_quantity + ': ' + (c.sku || ''))
          return
        }
        plan.push({ found, next })
      }
      for (const p of plan) p.found.packedQuantity = p.next
      this.playSuccess()
    },
    alertBeep (msg) {
      this.playBuzzer()
      Swal.fire({
        toast: true,
        position: 'top',
        type: 'warning',
        title: msg,
        timer: 3000,
        showConfirmButton: false,
      })
    },
    playBuzzer () {
      try { this.audioBuzzer.currentTime = 0; this.audioBuzzer.play() } catch (e) { /* ignore */ }
    },
    playSuccess () {
      try { this.audioSuccess.currentTime = 0; this.audioSuccess.play() } catch (e) { /* ignore */ }
    },
    runCommand (code) {
      const cmd = (code || '').trim().toUpperCase()
      switch (cmd) {
        case 'PH-SAVE':
          this.save(false)
          return true
        case 'PH-SAVEPRINT':
          this.save(true)
          return true
        case 'PH-PRINTAWB':
          if (this.orderInfo) this.printAwb()
          return true
        default:
          return false
      }
    },

    async save (isPrint) {
      if (!this.isAllPacked) return
      this.loading = true
      try {
        const body = this.buildSaveBody()
        const res = await this.savePackOrder(body)
        if (res && (res.status === 'success' || res.status === true)) {
          if (isPrint) {
            await this.printAwb()
          }
          AlertSuccess(this.use_language.ax_save_success)
          this.$emit('saved')
          this.clearOrder()
          this.focusScanOrder()
        } else {
          AlertError((res && res.error_description) || this.use_language.ax_save_failed)
        }
      } catch (e) {
        AlertError(this.use_language.save_failed_try_again)
      } finally {
        this.loading = false
      }
    },
    buildSaveBody () {
      const packageList = this.packages.map(pkg => {
        (pkg.items || []).forEach(x => { if (x.isStockShortage == null) x.isStockShortage = false })
        return {
          id: pkg.packageID,
          packageNo: pkg.packageNo,
          expressCompanyID: pkg.expressCompanyID,
          packedByStaffShopID: this.staffShopID ? parseInt(this.staffShopID) : null,
          weight: pkg.weight,
          cubic1: pkg.cubic1,
          cubic2: pkg.cubic2,
          cubic3: pkg.cubic3,
          boxName: pkg.boxName,
          codAmount: pkg.codAmount != null ? pkg.codAmount : null,
          codCharged: pkg.codCharged || null,
          codPercent: pkg.codPercent,
          deliveryPrice: pkg.deliveryPrice,
          boxPrice: pkg.boxPrice || null,
          packPrice: pkg.packPrice || null,
          remotePrice: pkg.remotePrice || null,
          bubblePrice: pkg.bubblePrice || null,
          otherServicePrice: pkg.otherServicePrice || null,
          discount: pkg.discount || null,
          branchExpressID: pkg.branchExpressID,
          shopExpressID: pkg.shopExpressID,
          productList: pkg.items,
        }
      })
      return {
        orderMasterID: this.orderInfo.id,
        packedByStaffShopID: this.staffShopID ? parseInt(this.staffShopID) : null,
        packageList: packageList,
        shopBankAccountID: null,
        photoList: null,
        serialList: [],
        serialToDeletedList: [],
      }
    },
    async printAwb () {
      const refSource = this.orderInfo.referenceSourceID
      const isMarketplace = refSource === 3 || refSource === 4 || refSource === 11
      const dataOUT = []

      for (const pkg of this.packages) {
        try {
          if (isMarketplace) {
            const data = await this.getAwbByPackageID(pkg.packageID, refSource)
            if (data && data.status === 'success') {
              if (data.bytes != null) dataOUT.push(data.bytes)
              else if (data.base64HTML != null) {
                const buf = await this.convertBase64HtmlToArrayBuffer(data.base64HTML)
                if (buf) dataOUT.push(buf)
              }
            }
          } else {
            const data = await this.createShipment(this.orderInfo.id, pkg.packageID)
            if (data && data.status === true && (data.message == null || data.message === '')) {
              dataOUT.push(data)
            }
          }
        } catch (e) { /* ignore */ }
      }

      if (dataOUT.length === 0) {
        AlertWarning(this.use_language.no_awb_to_print)
        return
      }

      if (isMarketplace) {
        const uint8List = dataOUT.map(it => (typeof it === 'string' ? this._base64ToUint8(it) : new Uint8Array(it)))
        if (printAgent.isReady() && printAgent.getConfiguredPrinter()) {
          let ok = true
          for (const u of uint8List) { if (!(await this._tryPrintPdfBytesViaAgent(u))) { ok = false; break } }
          if (ok) return
        }
        try { await this._mergeAndOpenPdf(uint8List) } catch (e) { /* ignore */ }
      } else {
        const key = Math.floor(Math.random() * 1000000 + 1).toString()
        localStorage.setItem('printAws', JSON.stringify(dataOUT))
        sessionStorage.setItem('encodedQueryKey_printAws', key)
        if (printAgent.isReady() && printAgent.getConfiguredPrinter()) {
          this._printAwsViaAgent(key)
        } else {
          window.open(`/printAws?key=${key}&autoprint=1`)
        }
      }
    },

    _printAwsViaAgent (key) {
      const paperWidth = 400
      const paperHeight = 400
      const iframe = document.createElement('iframe')
      iframe.style.cssText = 'position:fixed;width:820px;height:1200px;border:0;left:-9999px;top:0;opacity:0;pointer-events:none;z-index:-1'
      let done = false
      let timer = null

      const cleanup = () => {
        if (done) return
        done = true
        window.removeEventListener('message', handler)
        if (timer) { clearTimeout(timer); timer = null }
        try { if (iframe.parentNode) iframe.parentNode.removeChild(iframe) } catch (e) { /* ignore */ }
      }

      const fallback = () => {
        if (done) return
        cleanup()
        window.open(`/printAws?key=${key}&autoprint=1`)
      }

      const handler = async (event) => {
        const d = event.data
        if (!d || typeof d !== 'object') return
        if (d.type === 'PRINT_AWS_IMAGES') {
          if (timer) { clearTimeout(timer); timer = null }
          try {
            const images = Array.isArray(d.images) ? d.images : []
            for (const imgBase64 of images) {
              await printAgent.printImage(imgBase64, {
                paperWidth,
                paperHeight,
              })
            }
            cleanup()
          } catch (e) {
            console.warn('[print] agent ปริ้น /printAws ไม่สำเร็จ:', e.message)
            fallback()
          }
        } else if (d.type === 'PRINT_AWS_ERROR') {
          console.warn('[print] /printAws capture ล้มเหลว:', d.message)
          fallback()
        }
      }

      window.addEventListener('message', handler)
      timer = setTimeout(fallback, 10000)
      iframe.src = `/printAws?key=${key}&silent=1`
      document.body.appendChild(iframe)
    },

    async checkPrintAgent () {
      const start = Date.now()
      while (printAgent.getState() === 'unknown' && Date.now() - start < 1500) {
        await new Promise((r) => setTimeout(r, 100))
      }
      this.printAgentReady = printAgent.isReady()
      this.labelPrinterName = printAgent.getConfiguredPrinter() || ''
      this.labelSize = printAgent.getLabelSize()
      if (this.printAgentReady) this.verifyConfiguredPrinter()
    },

    async ensurePrinterOnOpen () {
      if (!printAgent.isReady()) {
        const ok = await printAgent.connect(3000)
        this.printAgentReady = printAgent.isReady()
        if (!ok) return
      }
      this.labelPrinterName = printAgent.getConfiguredPrinter() || ''
      this.labelSize = printAgent.getLabelSize()
      if (!printAgent.getConfiguredPrinter()) {
        this.openPrinterSetup()
      } else {
        this.verifyConfiguredPrinter()
      }
    },

    async openPrinterSetup () {
      this.selectedPrinterInDialog = this.labelPrinterName || null
      const isPreset = LABEL_SIZE_PRESETS.some((p) => p.id === this.labelSize)
      if (isPreset) {
        this.selectedLabelSizeInDialog = this.labelSize
      } else {
        const parsed = parseLabelSize(this.labelSize)
        if (parsed) {
          this.selectedLabelSizeInDialog = 'custom'
          this.customWidthMm = parsed.widthMm
          this.customHeightMm = parsed.heightMm
        } else {
          this.selectedLabelSizeInDialog = '100x150'
        }
      }
      this.printerSetupDialog = true
      if (printAgent.isReady()) {
        await this.loadPrinterList()
      } else {
        await this.connectPrintAgent()
      }
    },

    async loadPrinterList () {
      this.loadingPrinters = true
      try {
        this.availablePrinters = await printAgent.listPrinters()
      } catch (e) {
        console.warn('[printAgent] โหลดรายชื่อ printer ไม่ได้:', e.message)
        this.availablePrinters = []
      } finally {
        this.loadingPrinters = false
      }
    },

    savePrinterSelection () {
      if (!this.selectedPrinterInDialog) return
      let sizeStr = this.selectedLabelSizeInDialog
      if (sizeStr === 'custom') {
        const w = Number(this.customWidthMm)
        const h = Number(this.customHeightMm)
        if (!w || !h || w <= 0 || h <= 0) return
        sizeStr = `${w}x${h}`
      }
      printAgent.setConfiguredPrinter(this.selectedPrinterInDialog)
      printAgent.setLabelSize(sizeStr)
      this.labelPrinterName = this.selectedPrinterInDialog
      this.labelSize = sizeStr
      this.printerSetupDialog = false
      this.printerMissingWarning = false
      this.printerMissingReason = ''
      this.printerStatus = 'ready'
      this.startPrinterHealthCheck()
    },

    async connectPrintAgent () {
      this.connectingAgent = true
      try {
        const ok = await printAgent.connect(3000)
        if (!ok) {
          this.agentNotInstalledDialog = true
          return
        }
        await this.loadPrinterList()
      } finally {
        this.connectingAgent = false
      }
    },

    downloadPrintAgent () {
      window.open(this.printAgentDownloadUrl, '_blank', 'noopener,noreferrer')
      this.agentNotInstalledDialog = false
    },

    async verifyConfiguredPrinter () {
      const configured = printAgent.getConfiguredPrinter()
      if (!configured || !printAgent.hasLabelSizeConfigured()) return
      try {
        const status = await printAgent.checkPrinter(configured)
        this.printerStatus = status
        if (status === 'ready') { this.startPrinterHealthCheck(); return }
        this.printerMissingWarning = true
        this.printerMissingReason = status
      } catch (e) {
        console.warn('[printAgent] ตรวจสอบ printer ไม่สำเร็จ:', e.message)
      }
    },

    startPrinterHealthCheck () {
      this.stopPrinterHealthCheck()
      this.printerHealthInterval = setInterval(async () => {
        if (!this.printAgentReady || !this.labelPrinterName || this.printerSetupDialog) return
        try {
          const status = await printAgent.checkPrinter(this.labelPrinterName)
          const prev = this.printerStatus
          this.printerStatus = status
          if (status !== 'ready' && prev === 'ready') {
            this.printerMissingWarning = true
            this.printerMissingReason = status
            try { this.availablePrinters = await printAgent.listPrinters() } catch (err) { this.availablePrinters = [] }
            this.selectedPrinterInDialog = null
            this.selectedLabelSizeInDialog = this.labelSize
            this.printerSetupDialog = true
          }
        } catch (err) {
          console.warn('[printAgent] health check polling error:', err.message || err)
        }
      }, 10000)
    },

    stopPrinterHealthCheck () {
      if (this.printerHealthInterval) {
        clearInterval(this.printerHealthInterval)
        this.printerHealthInterval = null
      }
    },

    _base64ToUint8 (b64) {
      const binary = atob(b64)
      const len = binary.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
      return bytes
    },

    _getLabelPrintOptions () {
      const size = printAgent.getLabelSize()
      const parsed = parseLabelSize(size) || { widthMm: 100, heightMm: 150 }
      const paperWidth = Math.round((parsed.widthMm * 100) / 25.4)
      const paperHeight = Math.round((parsed.heightMm * 100) / 25.4)
      const PX_PER_MM = 16
      const MAX_DIM = 2400
      let targetWidth = Math.round(parsed.widthMm * PX_PER_MM)
      let targetHeight = Math.round(parsed.heightMm * PX_PER_MM)
      const cap = Math.min(1, MAX_DIM / Math.max(targetWidth, targetHeight))
      targetWidth = Math.round(targetWidth * cap)
      targetHeight = Math.round(targetHeight * cap)
      return { targetWidth, targetHeight, paperWidth, paperHeight }
    },

    async _tryPrintPdfBytesViaAgent (pdfBytes) {
      if (!printAgent.isReady() || !printAgent.getConfiguredPrinter()) return false
      try {
        const opt = this._getLabelPrintOptions()
        const images = await pdfToImages(pdfBytes, {
          targetWidth: opt.targetWidth,
          targetHeight: opt.targetHeight,
          paddingRatio: 0.03,
        })
        for (const imgBase64 of images) {
          await printAgent.printImage(imgBase64, {
            paperWidth: opt.paperWidth,
            paperHeight: opt.paperHeight,
          })
        }
        return true
      } catch (e) {
        console.warn('[print] agent ปริ้น PDF ไม่สำเร็จ:', e.message)
        return false
      }
    },

    async _tryPrintUrlViaAgent (url) {
      if (!printAgent.isReady() || !printAgent.getConfiguredPrinter()) return false
      try {
        const res = await fetch(url)
        if (!res.ok) return false
        const bytes = new Uint8Array(await res.arrayBuffer())
        const isPdf = bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
        if (isPdf) {
          return await this._tryPrintPdfBytesViaAgent(bytes)
        }
        const opt = this._getLabelPrintOptions()
        let binary = ''
        const chunk = 0x8000
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
        }
        await printAgent.printImage(btoa(binary), {
          paperWidth: opt.paperWidth,
          paperHeight: opt.paperHeight,
        })
        return true
      } catch (e) {
        console.warn('[print] agent ปริ้น URL ไม่สำเร็จ:', e.message)
        return false
      }
    },

    async _mergeAndOpenPdf (uint8List) {
      const pdfDoc = await PDFDocument.create()
      for (const u of uint8List) {
        const src = await PDFDocument.load(u)
        const pages = await pdfDoc.copyPages(src, src.getPageIndices())
        pages.forEach((p) => pdfDoc.addPage(p))
      }
      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      const w = window.open(pdfUrl)
      if (w) { try { w.print() } catch (e) { /* ignore */ } }
    },

    async convertBase64HtmlToArrayBuffer (base64Html) {
      try {
        const htmlString = decodeURIComponent(escape(atob(base64Html)))
        const html2canvas = (await import('html2canvas')).default
        const { default: jsPDF } = await import('jspdf')
        const iframe = document.createElement('iframe')
        iframe.style.visibility = 'hidden'
        document.body.appendChild(iframe)
        const iframedoc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document)
        if (!iframedoc) { document.body.removeChild(iframe); return null }
        iframedoc.body.innerHTML = htmlString
        const canvas = await html2canvas(iframedoc.body, {})
        const imgData = canvas.toDataURL('image/png')
        const doc = new jsPDF({ format: 'a4', unit: 'mm' })
        doc.addImage(imgData, 'PNG', 0, 0, 210, 297)
        document.body.removeChild(iframe)
        return doc.output('arraybuffer')
      } catch (e) {
        console.warn('[print] convert base64HTML ไม่สำเร็จ:', e.message)
        return null
      }
    },

    checkOrderStatusBeforePack (orderMasterID, packageID, trackingNo) {
      return axios.get(
        orderService_dotnet + 'Order/check-order-status-before-pack-shop' +
        '?StaffShopID=' + (this.staffShopID || 0) +
        '&OrderMasterID=' + (orderMasterID || 0) +
        '&PackageID=' + (packageID || 0) +
        '&TrackingNo=' + (trackingNo || ''),
        { headers: this.header_token }
      ).then(r => r.data)
    },
    getOrderMasterForPack (orderMasterID) {
      return axios.get(
        orderService_dotnet + 'Order/get-order-master-for-pack?OrderMasterID=' + orderMasterID,
        { headers: this.header_token }
      ).then(r => r.data)
    },
    getOrderPackageForPack (orderMasterID, packageID) {
      return axios.post(
        orderService_dotnet + 'Package/get-order-package-for-pack-shop',
        { orderID: orderMasterID, packageID: packageID, staffShopID: this.staffShopID ? parseInt(this.staffShopID) : null },
        { headers: this.header_token }
      ).then(r => r.data)
    },
    getStockShopByPack (barcode) {
      return axios.post(
        stockService_dotnet + 'Stock/get-stock-shop-by-pack',
        { shopID: this.orderInfo.shopID, search: barcode, OrderMasterID: this.orderInfo.id },
        { headers: this.header_token }
      ).then(r => r.data).catch(() => false)
    },
    getProductSetByScan (code) {
      return axios.post(
        stockService_dotnet + 'Stock/get-productset-by-scan',
        { shopID: this.orderInfo.shopID, search: code },
        { headers: this.header_token }
      ).then(r => r.data).catch(() => null)
    },
    savePackOrder (body) {
      return axios.post(
        orderService_dotnet + 'Order/save-pack-order-v2-shop',
        body,
        { headers: this.header_token }
      ).then(r => r.data)
    },
    createShipment (orderMasterID, packageID) {
      return axios.post(
        logistic_Service + 'Logistic/create-shipment-V2',
        {
          orderMasterID: orderMasterID,
          packageID: packageID,
          isCreateSubPackage: false,
          staffShopID: this.staffShopID ? parseInt(this.staffShopID) : null,
          isNeedAddress: true,
          isNeedProductList: false,
          isForceNewTrack: false,
        },
        { headers: this.header_token }
      ).then(r => r.data)
    },
    getAwbByPackageID (packageID, refSource) {
      return axios.post(
        ecomService_dotnet + 'Order/get-awb-by-packageid',
        {
          packageID: packageID,
          staffShopID: this.staffShopID ? parseInt(this.staffShopID) : null,
          documentType: 1,
          filetype: refSource === 4 ? 2 : 1,
        },
        { headers: this.header_token }
      ).then(r => r.data)
    },

    clearOrder () {
      this.scanCode = ''
      this.itemCode = ''
      this.orderInfo = null
      this.packages = []
      this.activeTab = 0
      this.show_pack = false
    },
    close () {
      this.clearOrder()
      this.show = false
    },
  },
}
</script>